// src/components/pageContent/components/PostAside.tsx
"use client";

import React from "react";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { useLayoutData } from "@/components/layout/LayoutDataProvider";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import { FacebookIcon, InstagramIcon, XIcon } from "@/components/ui/Icons";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function getString(obj: unknown, key: string): string | undefined {
  if (!isRecord(obj)) return undefined;
  const v = obj[key];
  return typeof v === "string" ? v : undefined;
}

function getRecord(obj: unknown, key: string): UnknownRecord | undefined {
  if (!isRecord(obj)) return undefined;
  const v = obj[key];
  return isRecord(v) ? v : undefined;
}

export default function PostAside() {
  const { global_text, company_data } = useLayoutData() as {
    global_text?: unknown;
    company_data?: unknown;
  };

  const postsText = (isRecord(global_text) ? global_text["posts"] : undefined) as unknown;

  // NEW: repeater of 3 images
  const inspiredRepeater =
    (isRecord(postsText) && Array.isArray(postsText["get_inspired_images"])
      ? postsText["get_inspired_images"]
      : isRecord(postsText) && Array.isArray(postsText["get_inspired_image_repeater"])
        ? postsText["get_inspired_image_repeater"]
        : []) as unknown[];

  const inspiredImages = Array.isArray(inspiredRepeater) ? inspiredRepeater.slice(0, 3) : [];

  const socials = getRecord(company_data, "social_links_fujifilm") ?? {};
  const instagram = getString(socials, "instagram");
  const facebook = getString(socials, "facebook");
  const x = getString(socials, "x") ?? getString(socials, "twitter");

  const inspirationHeading = isRecord(postsText) ? getString(postsText, "inspiration_heading") : undefined;
  const inspirationSubheading = isRecord(postsText) ? getString(postsText, "inspiration_subheading") : undefined;

  return (
    <>
      <div className="bg-fuji-grey p-6">
        {/* heading */}
        {inspirationHeading && (
          <div className="text-title-2 mb-3 font-bold">
            <SafeHtml content={inspirationHeading} />
          </div>
        )}

        {/* subheading */}
        {inspirationSubheading && (
          <div className="mb-6">
            <SafeHtml content={inspirationSubheading} flow />
          </div>
        )}

        {/* fujifilm socials only */}
        {(instagram || facebook || x) && (
          <div className="mt-6 flex items-center gap-4">
            {instagram && (
              <a
                href={instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Fujifilm on Instagram"
                className="inline-flex transition-opacity hover:opacity-80"
              >
                <InstagramIcon className="text-black" />
              </a>
            )}

            {facebook && (
              <a
                href={facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Fujifilm on Facebook"
                className="inline-flex transition-opacity hover:opacity-80"
              >
                <FacebookIcon className="text-black" />
              </a>
            )}

            {x && (
              <a
                href={x}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Fujifilm on X"
                className="inline-flex transition-opacity hover:opacity-80"
              >
                <XIcon className="text-black" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* NEW: 3 square images spanning full width */}
      {inspiredImages.length > 0 && (
        <div className="grid grid-cols-3 gap-0">
          {inspiredImages.map((item, idx: number) => {
            const img = (isRecord(item) ? (item["image"] ?? item) : item) as unknown;

            const urlRaw = isRecord(img) ? getString(img, "url") : undefined;
            const url = urlRaw ? toAbsoluteUrl(urlRaw) : "";
            if (!url) return null;

            const alt = (isRecord(img) ? getString(img, "alt") : undefined) ?? "";

            return (
              <div key={idx} className="relative aspect-square w-full overflow-hidden">
                <Image
                  src={url}
                  alt={alt}
                  fill
                  className="object-cover"
                  sizes="(min-width: 1024px) 8.33vw, 33.33vw"
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
